import { optionalArtPack } from '../content/art-packs';

export class ArtCleanupUnavailable extends Error {}
const useLock = (id: string) => `wordhuli-art-use:${id}`;
let desired: string | undefined, suspended = false, generation = 0;
let pending: AbortController | undefined, release: (() => void) | undefined;

function stop(): void {
  generation++; pending?.abort(); pending = undefined;
  release?.(); release = undefined;
}
function resume(): void {
  stop();
  if (suspended || !desired || !navigator.locks) return;
  const epoch = generation, controller = new AbortController(); pending = controller;
  void navigator.locks.request(useLock(desired), { mode: 'shared', signal: controller.signal }, async () => {
    if (epoch !== generation) return;
    await new Promise<void>(resolve => { release = resolve; });
  }).catch(() => { /* Unavailable coordination must not interrupt play. */ }).finally(() => {
    if (pending === controller) pending = undefined;
  });
}
export function setActiveArtPack(id?: string): void {
  if (id) optionalArtPack(id);
  if (desired === id && (pending || !id)) return;
  desired = id; resume();
}
export async function withUnusedArt(id: string, operation: () => Promise<void>): Promise<void> {
  if (!navigator.locks) throw new ArtCleanupUnavailable('Safe cross-window cleanup unavailable');
  await navigator.locks.request(useLock(id), { ifAvailable: true }, async lock => { if (lock) await operation(); });
}
export async function withUnusedArts<T>(ids: readonly string[], operation: (available: readonly string[]) => Promise<T>): Promise<T> {
  if (!navigator.locks) throw new ArtCleanupUnavailable('Safe cross-window cleanup unavailable');
  const acquire = async (index: number, available: string[]): Promise<T> => {
    if (index === ids.length) return operation(available);
    return navigator.locks.request(useLock(ids[index]!), { ifAvailable: true }, lock => acquire(index + 1, lock ? [...available, ids[index]!] : available));
  };
  return acquire(0, []);
}
if (typeof window !== 'undefined') {
  const suspend = () => { suspended = true; stop(); };
  const restore = () => { suspended = false; resume(); };
  window.addEventListener('pagehide', suspend); window.addEventListener('pageshow', restore);
  document.addEventListener('freeze', suspend); document.addEventListener('resume', restore);
}
